import React from 'react';
import { Redirect } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { Realm } from 'further-types/global';
import { ActionRef } from 'further-types/lp/action';
import { LpInvestorUpdateType } from 'further-types/lp/investor-update';

const SignUp = React.lazy(() => import('views/SignUp'));
const Login = React.lazy(() => import('views/Login'));
const AuthGateCopyProfile = React.lazy(
  () => import('views/AuthGate/CopyProfile'),
);
const AuthGateChooseAccount = React.lazy(
  () => import('views/AuthGate/ChooseAccount'),
);
const AdminImpersonationEntry = React.lazy(
  () => import('views/AdminImpersonationEntry'),
);
const Onbording = React.lazy(() => import('views/OnbordingFlow'));
const OnboardingSuccess = React.lazy(
  () => import('views/OnbordingFlow/Success'),
);
const Purchase = React.lazy(() => import('views/PurchaseFlow/Purchase'));
const PurchaseSuccess = React.lazy(() => import('views/PurchaseFlow/Success'));
const CoolOffPeriodWarning = React.lazy(
  () => import('views/PurchaseFlow/CooloffPeriodWarning'),
);
const MarketingPrivacyPolicyPage = React.lazy(
  () => import('views/Marketing/PrivacyPolicyPage'),
);

const FundUpdate = React.lazy(() => import('views/Portal/FundUpdate'));
const CustomUpdate = React.lazy(() => import('views/Portal/CustomUpdate'));
const SyndicateUpdate = React.lazy(
  () => import('views/Portal/SyndicateUpdate'),
);

const MyUpdates = React.lazy(() => import('views/Portal/MyUpdates'));

const ExitStatement = React.lazy(() => import('views/Portal/ExitStatement'));
const DeploymentUpdate = React.lazy(
  () => import('views/Portal/DeploymentUpdate'),
);
const RequestAccess = React.lazy(() => import('views/RequestAccess'));
const ForgetPassword = React.lazy(() => import('views/ForgetPassword'));
const ResetPassword = React.lazy(() => import('views/ResetPassword'));
const SummaryDashboard = React.lazy(
  () => import('views/Portal/SummaryDashboard'),
);
const Account = React.lazy(() => import('views/Portal/AccountPage'));
const InvestmentDashboard = React.lazy(
  () => import('views/Portal/InvestmentDashboard'),
);
const SyndicateInvestmentsDashboard = React.lazy(
  () => import('views/Portal/SyndicateInvestmentsDashboard'),
);
const Guide = React.lazy(() => import('views/Guide'));
const AssestmentError = React.lazy(
  () => import('views/PurchaseFlow/ErrorMessage'),
);
const GuideDetail = React.lazy(() => import('views/Guide/Detail'));
const KycPending = React.lazy(() => import('views/PurchaseFlow/KycPending'));
const SubscriptionExpired = React.lazy(
  () => import('views/PurchaseFlow/ExpiredSubscription'),
);
const MarketingTermsOfServicePage = React.lazy(
  () => import('views/Marketing/TermsOfServicePage'),
);
const PaymentUnsuccessful = React.lazy(
  () => import('views/PurchaseFlow/PaymentUnsuccessful'),
);
const CashBalance = React.lazy(() => import('views/Portal/CashBalance'));
const ShareHoldings = React.lazy(
  () => import('views/Portal/PortfolioHoldings'),
);
const FundOnboarding = React.lazy(() => import('views/FundOnboarding'));
const RedirectToPurchase = React.lazy(
  () => import('views/PurchaseFlow/RedirectToPurchase'),
);
const AdviserOnboarding = React.lazy(() => import('views/Adviser/Signup'));
const AdviserDashboard = React.lazy(() => import('views/Adviser/Dashboard'));
const InvestorCashBalance = React.lazy(
  () => import('views/Adviser/InvestorCashBalance'),
);
const InviteSuccess = React.lazy(
  () => import('views/Adviser/InviteInvestorSuccess'),
);
const InviteAdviser = React.lazy(() => import('views/Adviser/InviteInvestor'));
const OrganisationDashboard = React.lazy(
  () => import('views/Organisation/Dashboard'),
);
const InvestorTypeChange = React.lazy(() => import('views/InvestorTypeChange'));

const AdviserAccount = React.lazy(() => import('views/Adviser/Account'));
const OrganisationAccount = React.lazy(
  () => import('views/Organisation/Account'),
);
const ExpiredLink = React.lazy(
  () => import('views/ResetPassword/ExpiredLinkPage'),
);
const LpVehicle = React.lazy(() => import('views/Lp/Vehicle'));
const LpOnboarding = React.lazy(() => import('views/Lp/Onboarding'));
const LpOnboardingReview = React.lazy(
  () => import('views/Lp/OnboardingReview'),
);
const LpAnonymousSigningSuccess = React.lazy(
  () =>
    import('views/Lp/Onboarding/Steps/DocumentSigning/AnonymousSigningSuccess'),
);
const LpVehicleActivity = React.lazy(() => import('views/Lp/Vehicle/Activity'));
const MarketingLandingPage = React.lazy(
  () => import('views/Marketing/LandingPage'),
);
const MarketingPricingPage = React.lazy(
  () => import('views/Marketing/PricingPage'),
);

import { VehiclePageTab } from 'views/Lp/Vehicle/types';

type RouteComponent =
  | React.ComponentType
  | React.ComponentType<RouteComponentProps>
  | React.ComponentType<{ vehicleId: string }>;

export type CommonRouteConfig = {
  path: string;
  exact: boolean;
  name: string;
  component: RouteComponent;
  private?: boolean;
  isAdviserOnlyRoute?: boolean;
  isInvestorOnlyRoute?: boolean;
  isLoggedOutOnlyRoute?: boolean;
  redirectToSignUp?: boolean;
  hideForSubDomains?: boolean;
  onlyAllowedInSubDomain?: boolean;
  isOrganisationOnlyRoute?: boolean;
  restrictToInvestorRealm?: Realm;
  useLpPortalLayout?: boolean;
  useMarketingLayout?: boolean;
};

export const AUTH_GATE_COPY_PROFILE = '/auth-gate/copy-profile';
export const AUTH_GATE_CHOOSE_ACCOUNT = '/auth-gate/choose-account';

/** Marketing pages (main host only; see `hideForSubDomains`) */
export const MARKETING_LP_PATH = '/';
export const MARKETING_EIS_PATH = '/eis';
export const MARKETING_PRICING_PATH = '/pricing';

export const LP_ANONYMOUS_SIGNING_SUCCESS = '/lp-document-signed-success';

export const LP_ONBOARDING = '/lp-onboarding/:vehicleId/:vehicleApplicationId';
export const createLpOnboardingRoute = (
  vehicleId: string,
  vehicleApplicationId: string,
) => `/lp-onboarding/${vehicleId}/${vehicleApplicationId}`;

export const LP_ONBOARDING_REVIEW =
  '/lp-onboarding/:vehicleId/:vehicleApplicationId/review';
export const createLpOnboardingReviewRoute = (
  vehicleId: string,
  vehicleApplicationId: string,
) => `/lp-onboarding/${vehicleId}/${vehicleApplicationId}/review`;

export const LP_VEHICLE = '/vehicle';
export const LP_VEHICLE_ID = '/vehicle/:vehicleId';

const createVehicleIdRoute = (vehicleId: string) =>
  LP_VEHICLE_ID.replace(':vehicleId', vehicleId);

export const createVehicleRoute = (vehicleId: string, tab: VehiclePageTab) =>
  `${createVehicleIdRoute(vehicleId)}#${tab}`;
export const createVehicleOverviewRoute = (vehicleId: string) =>
  createVehicleRoute(vehicleId, VehiclePageTab.Overview);
export const createVehicleOnboardingRoute = (vehicleId: string) =>
  createVehicleRoute(vehicleId, VehiclePageTab.Onboarding);
export const createVehicleHoldingsRoute = (vehicleId: string) =>
  createVehicleRoute(vehicleId, VehiclePageTab.Holdings);
export const createVehicleLatestActivityRoute = (vehicleId: string) =>
  createVehicleRoute(vehicleId, VehiclePageTab.LatestActivity);

const LP_VEHICLE_HOLDING = `${LP_VEHICLE_ID}/holdings/:holdingId`;
export const createVehicleHoldingRoute = (
  vehicleId: string,
  holdingId: string,
) => `${createVehicleIdRoute(vehicleId)}/holdings/${holdingId}`;

type VehicleActivityRouteParams = {
  vehicleId: string;
  activityId: string;
} & (
  | {
      actionRef: ActionRef;
      updateType?: never;
    }
  | {
      actionRef?: never;
      updateType: LpInvestorUpdateType;
    }
);
const LP_VEHICLE_ACTIVITY = `${LP_VEHICLE_ID}/activities/:activityId`;
export const createVehicleActivityRoute = ({
  vehicleId,
  activityId,
  actionRef,
  updateType,
}: VehicleActivityRouteParams) => {
  const queryParams = new URLSearchParams();
  if (actionRef) queryParams.set('actionRef', actionRef);
  if (updateType) queryParams.set('updateType', updateType);

  return `${createVehicleIdRoute(
    vehicleId,
  )}/activities/${activityId}?${queryParams.toString()}`;
};

const commonRoutes: Array<CommonRouteConfig> = [
  {
    path: MARKETING_LP_PATH,
    exact: true,
    name: 'MarketingLp',
    component: MarketingLandingPage,
    hideForSubDomains: true,
    useMarketingLayout: true,
  },
  {
    path: MARKETING_EIS_PATH,
    exact: true,
    name: 'MarketingEis',
    component: MarketingLandingPage,
    hideForSubDomains: true,
    useMarketingLayout: true,
  },
  {
    path: MARKETING_PRICING_PATH,
    exact: true,
    name: 'MarketingPricing',
    component: MarketingPricingPage,
    hideForSubDomains: true,
    useMarketingLayout: true,
  },
  {
    path: '/',
    exact: true,
    name: 'SubdomainRoot',
    component: () => <Redirect to="/login" />,
    onlyAllowedInSubDomain: true,
  },
  {
    path: '/admin-impersonation-entry',
    exact: true,
    name: 'Admin impersonation entry',
    component: AdminImpersonationEntry,
    onlyAllowedInSubDomain: true,
  },
  {
    path: '/sign-up',
    exact: true,
    name: 'SignUp',
    component: SignUp,
    isLoggedOutOnlyRoute: true,
  },
  {
    path: '/login',
    exact: true,
    name: 'Login',
    component: Login,
    isLoggedOutOnlyRoute: true,
  },
  {
    path: AUTH_GATE_COPY_PROFILE,
    exact: true,
    name: 'Copy profile',
    component: AuthGateCopyProfile,
    private: true,
    onlyAllowedInSubDomain: true,
    isInvestorOnlyRoute: true,
  },
  {
    path: AUTH_GATE_CHOOSE_ACCOUNT,
    exact: true,
    name: 'Choose account',
    component: AuthGateChooseAccount,
    private: true,
    onlyAllowedInSubDomain: true,
  },
  {
    path: '/onboarding',
    exact: true,
    private: true,
    name: 'Onbording',
    component: Onbording,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/success',
    exact: true,
    private: true,
    name: 'OnboardingSuccess',
    component: OnboardingSuccess,
  },
  {
    path: '/purchase',
    exact: true,
    private: true,
    name: 'Purchase',
    component: Purchase,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/apply',
    exact: true,
    private: true,
    redirectToSignUp: true,
    name: 'Apply',
    component: RedirectToPurchase,
    isInvestorOnlyRoute: true,
  },
  {
    path: '/purchase-success',
    exact: true,
    private: true,
    name: 'Purchase',
    component: PurchaseSuccess,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/cool-off-period-warning',
    exact: true,
    private: true,
    name: 'Purchase',
    component: CoolOffPeriodWarning,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/privacy-policy',
    exact: true,
    name: 'Privacy',
    component: MarketingPrivacyPolicyPage,
    useMarketingLayout: true,
  },
  {
    path: '/forget-password',
    exact: true,
    name: 'ForgetPassword',
    component: ForgetPassword,
    isLoggedOutOnlyRoute: true,
  },
  {
    path: '/reset-password',
    exact: true,
    name: 'ResetPassword',
    component: ResetPassword,
  },
  {
    path: '/reset-password/expired-link',
    exact: true,
    name: 'ExpiredLink',
    component: ExpiredLink,
  },
  {
    path: '/request-access',
    exact: true,
    name: 'RequestAccess',
    component: RequestAccess,
    isLoggedOutOnlyRoute: true,
  },
  {
    path: '/signup',
    exact: true,
    name: 'SignUp',
    component: SignUp,
    isLoggedOutOnlyRoute: true,
  },
  {
    path: '/portal',
    private: true,
    exact: true,
    name: 'Dashboard',
    component: SummaryDashboard,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/dashboard',
    private: true,
    exact: true,
    name: 'Dashboard',
    component: () => <Redirect to="/portal" />,
  },
  {
    path: '/user/cash-balance/:firmId/:investorId',
    private: true,
    exact: true,
    name: 'Cash Balance',
    component: CashBalance,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/portfolio-holdings',
    private: true,
    exact: true,
    name: 'My underlying holdings',
    component: ShareHoldings,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/account',
    private: true,
    exact: true,
    name: 'Account',
    component: Account,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/investment/:id',
    private: true,
    exact: true,
    name: 'InvestmentDashboard',
    component: InvestmentDashboard,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/syndicate-investments/:groupingKey',
    private: true,
    exact: true,
    name: 'SyndicateInvestmentsDashboard',
    component: SyndicateInvestmentsDashboard,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/fund-update/:reportingId/:investmentId',
    private: true,
    exact: true,
    name: 'Fund Update',
    component: FundUpdate,
  },
  {
    path: '/user/custom-update/:customUpdateId/:investmentId?',
    private: true,
    exact: true,
    name: 'Custom Update',
    component: CustomUpdate,
  },
  {
    path: '/user/exit-statement/:exitStatementId/:investmentId?',
    private: true,
    exact: true,
    name: 'Exit Update',
    component: ExitStatement,
  },
  {
    path: '/user/deployment-update/:deploymentUpdateId/:investmentId?',
    private: true,
    exact: true,
    name: 'Deployment Update',
    component: DeploymentUpdate,
  },
  // the route below is not actively used, having been replaced by the one above. The
  // The difference is that the one below has a fundId in the path, but this is not
  // actually required. Because some investors may have old emails that link to this
  // route, we will keep it here for some time.
  {
    path: '/user/fund-update/:reportingId/:fundId/:investmentId',
    private: true,
    exact: true,
    name: 'Fund Update',
    component: FundUpdate,
  },
  {
    path: '/user/syndicate-update/:reportingId/:groupingKey',
    private: true,
    exact: true,
    name: 'Syndicate Update',
    component: SyndicateUpdate,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/user/my-updates',
    private: true,
    exact: true,
    name: 'My Updates',
    component: MyUpdates,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/guides',
    exact: true,
    name: 'Guide',
    component: Guide,
    hideForSubDomains: true,
  },
  {
    path: '/guides/:id/:itemId',
    exact: true,
    name: 'GuideDetail',
    component: GuideDetail,
    hideForSubDomains: true,
  },
  {
    path: '/purchase-failure',
    private: true,
    exact: true,
    name: 'Purchase',
    component: AssestmentError,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/kyc-pending',
    private: true,
    exact: true,
    name: 'Purchase',
    component: KycPending,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/subscription-expired',
    private: true,
    exact: true,
    name: 'Subscription Expired',
    component: SubscriptionExpired,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/payment-unsuccessful',
    private: true,
    exact: true,
    name: 'Payment Unsuccessful',
    component: PaymentUnsuccessful,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
  },
  {
    path: '/terms-conditions',
    exact: true,
    name: 'Terms and conditions',
    component: MarketingTermsOfServicePage,
    useMarketingLayout: true,
  },
  // This is used by funds to send links to investors so it takes them
  // to the purchase flow for a specific fund. We have a very similar
  // link /apply, but this does not specify a fund. We should combine
  // this link and /apply in the future.
  {
    path: '/fund-onboarding/:fundSlug',
    exact: true,
    private: true,
    name: 'FundOnboarding',
    component: FundOnboarding,
    onlyAllowedInSubDomain: true,
  },
  {
    path: '/adviser-onboarding',
    exact: true,
    private: true,
    name: 'AdviserOnboarding',
    isAdviserOnlyRoute: true,
    component: AdviserOnboarding,
  },
  {
    path: '/invite-investor',
    exact: true,
    name: 'InviteAdviser',
    isAdviserOnlyRoute: true,
    private: true,
    component: InviteAdviser,
  },
  {
    path: '/adviser-dashboard',
    exact: true,
    private: true,
    name: 'AdviserDashboard',
    isAdviserOnlyRoute: true,
    component: AdviserDashboard,
  },
  {
    path: '/adviser/account',
    exact: true,
    private: true,
    name: 'AdviserAccount',
    isAdviserOnlyRoute: true,
    component: AdviserAccount,
  },
  {
    path: '/adviser/investor-cash-balance/:investorId/:firmId',
    exact: true,
    private: true,
    name: 'InvestorCashBalance',
    isAdviserOnlyRoute: true,
    component: InvestorCashBalance,
  },
  {
    path: '/adviser/investment/:id/dashboard',
    exact: true,
    name: 'AdviserInvestmentDashboard',
    isAdviserOnlyRoute: true,
    component: InvestmentDashboard,
  },
  {
    path: '/invite-success',
    exact: true,
    name: 'invite-success',
    isAdviserOnlyRoute: true,
    component: InviteSuccess,
    private: true,
  },
  {
    path: '/org-dashboard',
    exact: true,
    name: 'OrganisationDashboard',
    component: OrganisationDashboard,
    isOrganisationOnlyRoute: true,
    private: true,
  },
  {
    path: '/organisation/account',
    exact: true,
    name: 'OrganisationAccount',
    component: OrganisationAccount,
    isOrganisationOnlyRoute: true,
    private: true,
  },
  {
    path: '/investor-type-change',
    name: 'InvestorTypeChange',
    component: InvestorTypeChange,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
    private: true,
  },
  {
    path: '/investor-type-change/:investorType',
    name: 'InvestorTypeChange',
    component: InvestorTypeChange,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Eis,
    private: true,
  },
  {
    path: LP_VEHICLE,
    name: 'LpVehicle',
    component: LpVehicle,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
  {
    path: LP_VEHICLE_ID,
    name: 'LpVehicle',
    component: LpVehicle,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
  {
    path: LP_VEHICLE_HOLDING,
    name: 'LpVehicleHolding',
    component: LpVehicle,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
  {
    path: LP_ONBOARDING,
    name: 'LpOnboarding',
    component: LpOnboarding,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
  {
    path: LP_ONBOARDING_REVIEW,
    name: 'LpOnboardingReview',
    component: LpOnboardingReview,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
  {
    exact: true,
    path: LP_ANONYMOUS_SIGNING_SUCCESS,
    name: 'LpAnonymousSigningSuccess',
    component: LpAnonymousSigningSuccess,
    private: false,
    useLpPortalLayout: true,
  },
  {
    path: LP_VEHICLE_ACTIVITY,
    name: 'LpVehicleActivity',
    component: LpVehicleActivity,
    exact: true,
    isInvestorOnlyRoute: true,
    restrictToInvestorRealm: Realm.Lp,
    private: true,
  },
];

export default commonRoutes;
